import React, {useContext, useEffect} from "react";
import {PageEnum} from "../../../../types/IPage.ts";
import CustomPopup from "../../../../components/CustomPopup";
import './MyMatchDialog.css'
import {ThemeContext} from "../../../../providers/themeContext/theme-context.tsx";
import {apiService} from "../../../../common/utils/http/api-service.ts";
import {IVSPlayerRoomInfo} from "../../../../common/utils/http/IResponse.ts";
import debounce from "lodash/debounce";
import {useUserContext} from "../../../../providers/userProvider/user-context.tsx";
import {formatTime} from "../../../../common/utils/date-format.ts";
import useLoading from "../../../../providers/loadingContext/useLoading.ts";
import {GAEvents} from "../../../../common/utils/GA4/GAEvents.ts";
import {useMusicContext} from "../../../../providers/musicProvider/music-player-context.tsx";
import Avatar from "../../../../components/Avatar/Avatar.tsx";
import CustomAvatar from "../../../../components/CustomAvatar/CustomAvatar.tsx";
import {isEmptyOrNullOrUndefined} from "../../../../common/utils/Datautil.ts";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    switchPage: (page: PageEnum) => void;
    share: (room: IVSPlayerRoomInfo) => void;
    setVsPlayerInfo: (value: IVSPlayerRoomInfo) => void;
    showOtherUserProfile: (userId: string) => void;
}

type IvsPlayer = {
    id?: string,
    name?: string,
    avatar?: string,
    steps?: number,
    time?: number,
    win?: boolean,
    result?: boolean,
    avatarFrame?: string,
}

// vs player我的游戏历史
const MyMatchDialog: React.FC<Props> = ({
                                            isOpen,
                                            onClose,
                                            switchPage,
                                            setVsPlayerInfo,
                                            share,
                                            showOtherUserProfile
                                        }) => {
        // 主题管理器
        const {imgUrls} = useContext(ThemeContext);
        // 全局音乐provider
        const musicManager = useMusicContext();
        const {showLoading, hideLoading} = useLoading();
        // 切换按钮
        const [switchIndex, setSwitchIndex] = React.useState<number>(1)
        // 我的游戏历史列表
        const [myMatchedList, setMyMatchedList] = React.useState<IVSPlayerRoomInfo[]>([])
        // 全局用户信息provider
        const {user} = useUserContext();
        useEffect(() => {
            if (isOpen) {
                // GA埋点
                GAEvents.myMatchesShow()

                getMyMatchedList();
            }
        }, [isOpen]);

        // 获取我的matched列表
        const getMyMatchedList = debounce(async () => {
            try {
                showLoading()
                const response = await apiService.getVsPlayerRoomList();
                if (response.code === 200) {
                    setMyMatchedList(response.data)
                }
            } finally {
                hideLoading()
            }
        }, 100)

        // 切换按钮方法
        const switchBtn = (index: number) => {
            setSwitchIndex(index)
            musicManager.playButtonSound()
        }

        // 切换页面进入游戏
        const handleEnterGame = (item: IVSPlayerRoomInfo) => {
            // GA埋点
            GAEvents.myMatchesClickEnter()

            const status = handleEnterStatus(item.ownerUserId, item.invitedUserId, item.ownerWin, item.invitedWin)
            if (!status) {
                return;
            }
            setVsPlayerInfo(item)
            switchPage(PageEnum.VS_PLAYER_GAME_PAGE)
        }

        // 判断左侧要返回的数据
        const handleLeftItem = (item: IVSPlayerRoomInfo): IvsPlayer => {
            if (user?.id === item.ownerUserId) {
                return {
                    id: item.ownerUserId,
                    name: item.ownerCgtnName ?? item.ownerGuestName ?? '-',
                    avatar: item.ownerCgtnAvatar ?? imgUrls.defaultAvatarUrl,
                    win: item.ownerWin,
                    steps: item.ownerGuessTimes,
                    time: item.ownerPlayTime,
                    result: item.result === 1,
                    avatarFrame: item.avatarFrame,
                }
            } else {
                return {
                    id: item.invitedUserId,
                    name: item.invitedCgtnName ?? item.invitedGuestName ?? '-',
                    avatar: item.invitedCgtnAvatar ?? imgUrls.defaultAvatarUrl,
                    win: item.invitedWin,
                    steps: item.invitedGuessTimes,
                    time: item.invitedPlayTime,
                    result: item.result === 2,
                    avatarFrame: item.avatarFrame,
                }
            }
        }

        // 判断右要返回的数据
        const handleRightItem = (item: IVSPlayerRoomInfo): IvsPlayer => {
            if (user?.id === item.invitedUserId) {
                return {
                    id: item.ownerUserId,
                    name: item.ownerCgtnName ?? item.ownerGuestName ?? '-',
                    avatar: item.ownerCgtnAvatar ?? imgUrls.defaultAvatarUrl,
                    win: item.ownerWin,
                    steps: item.ownerGuessTimes,
                    time: item.ownerPlayTime,
                    result: item.result === 1,
                    avatarFrame: item.avatarFrame,
                }
            } else {
                return {
                    id: item.invitedUserId,
                    name: item.invitedCgtnName ?? item.invitedGuestName ?? '-',
                    avatar: item.invitedCgtnAvatar ?? imgUrls.defaultAvatarUrl,
                    win: item.invitedWin,
                    steps: item.invitedGuessTimes,
                    time: item.invitedPlayTime,
                    result: item.result === 2,
                    avatarFrame: item.avatarFrame,
                }
            }
        }


        // 判断左侧要显示什么文字
        const handleLeftTextTitle = (item: IVSPlayerRoomInfo): string => {

            let text = "You "

            if (item.result === 0) {
                text += 'Draw'
                return text;
            }

            if (item.ownerUserId === user?.id) {
                if (item.result === 1) {
                    text += 'Win!'
                    return text;
                } else if (item.result === 2) {
                    text += 'Lose'
                    return text;
                }
            }

            if (item.invitedUserId === user?.id) {
                if (item.result === 2) {
                    text += 'Win!'
                    return text;
                } else if (item.result === 1) {
                    text += 'Lose'
                    return text;
                }
            }

            return text;
        }

        // 判断右侧要显示什么文字
        const handleRightTextTitle = (item: IVSPlayerRoomInfo): string => {
            let text = ""

            if (item.result === 0) {
                text += 'Draw'
                return text;
            }

            if (item.ownerUserId === user?.id) {
                if (item.result === 2) {
                    text += 'Win!'
                    return text;
                } else if (item.result === 1) {
                    text += 'Lose'
                    return text;
                }
            }

            if (item.invitedUserId === user?.id) {
                if (item.result === 1) {
                    text += 'Win!'
                    return text;
                } else if (item.result === 2) {
                    text += 'Lose'
                    return text;
                }
            }

            return text;
        }

        // 根据数据判断是否可以进入游戏
        const handleEnterStatus = (ownerId?: string, invitedId?: string, ownerIdWin?: boolean, invitedWin?: boolean): boolean => {
            if (user?.id === ownerId) {
                // 如果是创建人
                return !(ownerIdWin === true || ownerIdWin === false);
            }

            if (user?.id === invitedId) {
                // 如果是被邀请人
                return !(invitedWin === true || invitedWin === false);
            }
            return false;
        }

        return (
            <CustomPopup isOpen={isOpen} onClose={onClose}>
                <div className="my-match-dialog-container">
                    <img className="my-match-dialog-bg" src={imgUrls.vsPlayMyMatchDialogBgPic} alt="pic"/>
                    {/*标题*/}
                    <div
                        className="font-size-16 color-white my-roboto-bold text-center margin-top-11 height-26 display-flex-row display-flex-justify-content-center display-flex-align-items-center">
                        My Matches
                        <img onClick={onClose} src={imgUrls.closeBtnWhiteUrl} className="exit-close-btn"
                             alt="pic"/>
                    </div>
                    <div
                        className="width-273 height-521 bg-img-cover-no-repeat-center margin-left-right-auto margin-top-14"
                        style={{backgroundImage: `url(${imgUrls.vsPlayMyMatchCardBg})`}}>
                        <div className="width-233 display-flex-column margin-left-right-auto">
                            <div className="bottom-border-line width-233 display-flex-row height-36 margin-top-10">
                                <div onClick={() => switchBtn(1)}
                                     className={`${switchIndex === 1 ? '' : 'color-grey'} font-size-14 my-roboto-medium width-116 height-36 display-flex-row display-flex-justify-content-center display-flex-align-items-center`}>Completed
                                </div>
                                <div onClick={() => switchBtn(2)}
                                     className={`${switchIndex === 2 ? '' : 'color-grey'} font-size-14 my-roboto-medium  width-116 height-36 display-flex-row display-flex-justify-content-center display-flex-align-items-center`}>
                                    In Progress
                                </div>
                            </div>
                            {/*切换指示器*/}
                            <div
                                className={`width-116 my-match-dialog-indicator margin-top-down-2 ${switchIndex === 1 ? 'margin-right-auto' : 'margin-left-auto'}`}/>
                            {/*Completed列表*/}
                            {
                                switchIndex === 1 ?
                                    <div className="height-472 display-flex-column">
                                        <div className="height-458 overflow-y-auto margin-top-8">
                                            {myMatchedList.map((item) => {
                                                if (item.completed) {
                                                    return <div
                                                        className="border-radius-6 margin-bottom-8 bg-color-white height-131 width-233">
                                                        {/*头像部分*/}
                                                        <div className="height-46 position-relative ">
                                                            <div
                                                                className="width-134 height-46 position-absolute position-left-0 display-flex-row display-flex-align-items-center"
                                                                style={{
                                                                    backgroundImage: `url(${((item.result === 1 && item.ownerUserId === user?.id) || (item.result === 2 && item.invitedUserId === user?.id)) ? imgUrls.vsPlayMyMatchLeftBlueBg : imgUrls.vsPlayMyMatchLeftGreyBg})`,
                                                                    backgroundSize: '100% 100%',
                                                                    backgroundRepeat: 'no-repeat',
                                                                    backgroundPosition: 'left'

                                                                }}>
                                                                <Avatar className="margin-left-8 height-27 width-27"/>
                                                                <div
                                                                    className="margin-left-2 my-roboto-bold font-size-14 color-white text-italic">
                                                                    {handleLeftTextTitle(item)}
                                                                </div>
                                                            </div>
                                                            <div
                                                                className="width-134 height-46 position-absolute right-0 display-flex-row display-flex-align-items-center display-flex-justify-content-center"
                                                                style={{
                                                                    backgroundImage: `url(${((item.result === 2 && item.ownerUserId === user?.id) || (item.result === 1 && item.invitedUserId === user?.id)) ? imgUrls.vsPlayMyMatchRightPinkBg : imgUrls.vsPlayMyMatchRightGreyBg})`,
                                                                    backgroundSize: '100% auto',
                                                                    backgroundRepeat: 'no-repeat',
                                                                    backgroundPosition: 'left'
                                                                }}>
                                                                <CustomAvatar
                                                                    onClick={() => showOtherUserProfile(handleRightItem(item).id ?? '')}
                                                                    className="margin-left-32 height-27 width-27"
                                                                    avatar={handleRightItem(item).avatar}
                                                                    avatarFrame={handleRightItem(item).avatarFrame}/>
                                                                <div
                                                                    className="margin-left-4 my-roboto-bold font-size-14 color-white text-italic">
                                                                    {handleRightTextTitle(item)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/*答案*/}
                                                        <div
                                                            className="display-flex-row display-flex-align-items-center display-flex-justify-content-center my-match-dialog-answer-bg-color height-21">
                                                            <div
                                                                className="color-grey my-roboto-regular font-size-10">Answer:
                                                            </div>
                                                            <div
                                                                className="margin-left-4 my-match-dialog-answer-text-color my-roboto-bold font-size-10">
                                                                {item.correctWord}
                                                            </div>
                                                        </div>
                                                        {/*数据*/}
                                                        <div
                                                            className="margin-top-4 width-206 height-50 margin-left-right-auto display-flex-row display-flex-space-between">
                                                            <div className="display-flex-column display-flex-space-between">
                                                                <div
                                                                    className="my-roboto-bold width-93 ellipsis font-size-11">{handleLeftItem(item).name}</div>
                                                                <div className="display-flex-row">
                                                                    <div
                                                                        className="my-roboto-regular font-size-10 color-grey ">
                                                                        Attempts:
                                                                    </div>
                                                                    <div
                                                                        className="margin-left-3 color-blue font-size-10 my-roboto-bold">{handleLeftItem(item).steps}
                                                                    </div>
                                                                </div>
                                                                {handleLeftItem(item).win === false?
                                                                    <div className="display-flex-row">
                                                                        <div className={`font-size-10 margin-left-3 color-blue my-roboto-bold`}>Didn't make it</div>
                                                                    </div>
                                                                    :
                                                                    <div className="display-flex-row">
                                                                    <div
                                                                        className="my-roboto-regular font-size-10 color-grey ">
                                                                        Time used:
                                                                    </div>
                                                                    <div
                                                                        className={`${handleLeftItem(item).win === false ? 'font-size-9' : 'font-size-10'} margin-left-3 color-blue my-roboto-bold`}>
                                                                        {handleLeftItem(item).win === false ? "Didn't make it" : formatTime(handleLeftItem(item).time ?? 0)}
                                                                    </div>
                                                                </div>}
                                                            </div>
                                                            <div
                                                                className="display-flex-column display-flex-space-between text-end">
                                                                <div
                                                                    className="my-roboto-bold width-93 ellipsis font-size-11">{handleRightItem(item).name}</div>
                                                                <div className="display-flex-row">
                                                                    <div
                                                                        className="my-roboto-regular font-size-10 color-grey margin-left-auto">
                                                                        Attempts:
                                                                    </div>
                                                                    <div
                                                                        className="margin-left-3 color-blue font-size-10 my-roboto-bold">{handleRightItem(item).steps ?? "-"}
                                                                    </div>
                                                                </div>
                                                                {
                                                                    handleRightItem(item).win === false ?
                                                                        <div className="display-flex-row">
                                                                            <div className={`font-size-10 color-blue my-roboto-bold margin-left-auto`}>Didn't make it</div>
                                                                        </div>
                                                                        :
                                                                    <div className="display-flex-row">
                                                                    <div
                                                                        className="my-roboto-regular font-size-10 color-grey margin-left-auto">
                                                                        Time used:
                                                                    </div>
                                                                    <div
                                                                        className={`${handleRightItem(item).win === false ? 'font-size-9' : 'font-size-10'} margin-left-3 color-blue my-roboto-bold`}>{handleRightItem(item).win === false ? "Didn't make it" : formatTime(handleRightItem(item).time ?? 0)}
                                                                    </div>
                                                                </div>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                            })}
                                        </div>
                                    </div>
                                    :
                                    <div className="height-472 display-flex-column">
                                        {/*进行中*/}
                                        <div className="height-458 overflow-y-auto margin-top-8">
                                            {myMatchedList.map((item) => {
                                                if (!item.completed) {
                                                    return <div key={item.id}
                                                                className="margin-bottom-8 height-147 width-233 bg-color-white border-radius-6 display-flex-column">
                                                        <div
                                                            className="width-233 display-flex-row display-flex-space-between position-relative">
                                                            <div
                                                                className="width-116 height-108 display-flex-column display-flex-align-items-center display-flex-justify-content-center">
                                                                <Avatar className="margin-left-8 height-33 width-33"/>
                                                                <div
                                                                    className="font-size-11 my-roboto-bold margin-top-4 width-80 ellipsis">{handleLeftItem(item).name}
                                                                </div>
                                                                <div
                                                                    className="margin-top-6 display-flex-row display-flex-align-items-center display-flex-justify-content-center">
                                                                    <div className="color-grey font-size-10">Attempts:</div>
                                                                    <div
                                                                        className="color-blue font-size-11 my-roboto-bold">{handleLeftItem(item).steps ?? '-'}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="margin-top-6 display-flex-row display-flex-align-items-center display-flex-justify-content-center">
                                                                    <div className="color-grey font-size-10">Time used:</div>
                                                                    <div
                                                                        className={`${handleLeftItem(item).win === false? 'font-size-9 my-roboto-bold' : 'font-size-11 my-roboto-bold'} color-blue `}>{handleLeftItem(item).win === false? "Didn't make it" :handleLeftItem(item).time ? formatTime(handleLeftItem(item).time ?? 0) : '-'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="my-match-dialog-vertical-line"/>
                                                            <div
                                                                className="my-match-dialog-VS-text my-roboto-bold font-size-17">VS
                                                            </div>
                                                            <div
                                                                className="width-116 height-108 display-flex-column display-flex-align-items-center display-flex-justify-content-center">
                                                                <CustomAvatar
                                                                    onClick={!isEmptyOrNullOrUndefined(handleRightItem(item).id) ? () => showOtherUserProfile(handleRightItem(item).id ?? '') : () => {
                                                                    }}
                                                                    className=" height-33 width-33"
                                                                    avatar={handleRightItem(item).avatar}
                                                                    avatarFrame={handleRightItem(item).avatarFrame}/>

                                                                <div
                                                                    className="font-size-11 width-80 ellipsis my-roboto-bold margin-top-4 text-center">{handleRightItem(item).name ?? '-'}
                                                                </div>
                                                                <div
                                                                    className="margin-top-6 display-flex-row display-flex-align-items-center display-flex-justify-content-center">
                                                                    <div className="color-grey font-size-10">Attempts:</div>
                                                                    <div
                                                                        className="color-blue font-size-11 my-roboto-bold">{handleRightItem(item).steps ?? '-'}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="margin-top-6 display-flex-row display-flex-align-items-center display-flex-justify-content-center">
                                                                    <div className="color-grey font-size-10">Time used:</div>
                                                                    <div
                                                                        className={`${handleRightItem(item).win === false? 'font-size-9 my-roboto-bold' : 'font-size-11 my-roboto-bold'} color-blue `}>{handleRightItem(item).win === false ? "Didn't make it" :handleRightItem(item).time ? formatTime(handleRightItem(item).time ?? 0) : '-'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {item.invitedUserId ?
                                                            <div onClick={() => handleEnterGame(item)}
                                                                 className={` ${handleEnterStatus(item?.ownerUserId, item.invitedUserId, item?.ownerWin, item.invitedWin) ? 'bg-color-blue-btn' : 'bg-color-grey-btn'} color-white font-size-11 my-roboto-bold border-radius-4  height-26 width-206 margin-left-right-auto display-flex-row display-flex-align-items-center display-flex-justify-content-center`}>
                                                                Enter The Match
                                                            </div> :
                                                            <div
                                                                className="height-26 width-206 margin-left-right-auto display-flex-row  display-flex-space-between">
                                                                <div onClick={() => share(item)}
                                                                     className="height-26 width-26 border-radius-5 bg-color-liner-green-blue display-flex-column display-flex-align-items-center display-flex-justify-content-center">
                                                                    <img className="width-14 height-14"
                                                                         src={imgUrls.shareIconBlueUrl} alt="pic"/>
                                                                </div>
                                                                <div onClick={() => handleEnterGame(item)}
                                                                     className={`${handleEnterStatus(item?.ownerUserId, item.invitedUserId, item?.ownerWin, item.invitedWin) ? 'bg-color-blue-btn' : 'bg-color-grey-btn'} color-white font-size-11 my-roboto-bold border-radius-4 height-26 width-170 display-flex-row display-flex-align-items-center display-flex-justify-content-center`}>
                                                                    Enter The Match
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                }
                                            })}

                                        </div>
                                    </div>
                            }
                        </div>

                    </div>
                </div>
            </CustomPopup>
        )
    }
;

export default MyMatchDialog;